import { GroupActions } from './groupActions';
import { GroupActionTypes } from './groupActionTypes';
import { IGroupState, initialGroupState, IGroupMember } from './groupState';

function addIfMissing<T>(element: T, elements: T[]) {
    if (!elements || !elements.length) { return [element]; }
    return elements.indexOf(element) > -1 ? elements : [...elements, element];
}

function unique<T>(value: T, index: number, self: T[]) {
    return self.indexOf(value) === index;
}

export const groupReducer = (state = initialGroupState, action: GroupActions): IGroupState => {
    switch (action.type) {
        case GroupActionTypes.CANCEL_PERSON_COMPLETED:
            {
                return {
                    ...state,
                    meetings: {
                        ...state.meetings,
                        byId: {
                            ...state.meetings.byId,
                            [action.payload.groupId]: state.meetings.byId[action.payload.groupId]
                                .map(m => m.meetingId !== action.payload.meetingId ? m : { ...m, participantStatuses: m.participantStatuses.map(p => p.member.diNumber !== action.payload.personId ? p : { ...p, status: 'DECLINED' }) })
                        }
                    }
                }
            }
        case GroupActionTypes.SIGNUP_PERSON_COMPLETED:
            {
                return {
                    ...state,
                    meetings: {
                        ...state.meetings,
                        byId: {
                            ...state.meetings.byId,
                            [action.payload.groupId]: state.meetings.byId[action.payload.groupId]
                                .map(m => m.meetingId !== action.payload.meetingId ? m : { ...m, participantStatuses: m.participantStatuses.map(p => p.member.diNumber !== action.payload.personId ? p : { ...p, status: 'ACCEPTED' }) })
                        }
                    }
                }
            }
        case GroupActionTypes.REMOVE_MEETING_DOCUMENT_COMPLETED:
            {
                return {
                    ...state,
                    meetings: {
                        ...state.meetings,
                        byId: {
                            ...state.meetings.byId,
                            [action.payload.groupId]: state.meetings.byId[action.payload.groupId]
                                .map(m => m.meetingId !== action.payload.meetingId ? m : { ...m, documents: m.documents.filter(d => d.id !== action.payload.documentId) })
                        }
                    }
                }
            }
        case GroupActionTypes.ADD_FAVORITE_ATTACHMENT_COMPLETED:
            {
                return {
                    ...state,
                    messages: {
                        ...state.messages,
                        byId: {
                            ...state.messages.byId,
                            [action.payload.groupId]: state.messages.byId[action.payload.groupId]
                                .map(m => m.messageId !== action.payload.messageId ? m : {
                                    ...m, attachments: m.attachments.map(a => a.id !== action.payload.attachmentId ? a : { ...a, isFavorite: true })
                                })
                        }
                    }
                }
            }
        case GroupActionTypes.REMOVE_FAVORITE_ATTACHMENT_COMPLETED:
            {
                return {
                    ...state,
                    messages: {
                        ...state.messages,
                        byId: {
                            ...state.messages.byId,
                            [action.payload.groupId]: state.messages.byId[action.payload.groupId]
                                .map(m => m.messageId !== action.payload.messageId ? m : {
                                    ...m, attachments: m.attachments.map(a => a.id !== action.payload.attachmentId ? a : { ...a, isFavorite: false })
                                })
                        }
                    }
                }
            }
        case GroupActionTypes.FETCH_GROUP_MEETING_COMPLETED:
            {
                return {
                    ...state,
                    meetings: {
                        allIds: addIfMissing(action.payload.meeting.groupId, state.meetings.allIds),
                        byId: {
                            ...state.meetings.byId,
                            [action.payload.meeting.groupId]:
                                state.meetings.byId[action.payload.meeting.groupId] && state.meetings.byId[action.payload.meeting.groupId].length && state.meetings.byId[action.payload.meeting.groupId].some(m => m.meetingId === action.payload.meeting.meetingId)
                                    ? state.meetings.byId[action.payload.meeting.groupId].map(m => m.meetingId === action.payload.meeting.meetingId ? action.payload.meeting : m)
                                    : state.meetings.byId[action.payload.meeting.groupId] && state.meetings.byId[action.payload.meeting.groupId].length
                                        ? [...state.meetings.byId[action.payload.groupId], action.payload.meeting]
                                        : [action.payload.meeting]
                        }
                    }
                }
            }
        case GroupActionTypes.FETCH_GROUP_MEETINGS_COMPLETED: {
            return {
                ...state,
                meetings: {
                    ...state.meetings,
                    allIds: addIfMissing(action.payload.id, state.meetings.allIds),
                    byId: {
                        ...state.meetings.byId,
                        [action.payload.id]: action.payload.meetings
                    }
                }
            }
        }
        case GroupActionTypes.ADD_FAVORITE_DOCUMENT_COMPLETED:
            {
                return {
                    ...state,
                    meetings: {
                        ...state.meetings,
                        byId: {
                            ...state.meetings.byId,
                            [action.payload.groupId]: state.meetings.byId[action.payload.groupId]
                                .map(m => m.meetingId !== action.payload.meetingId ? m : {
                                    ...m, documents: m.documents.map(a => a.id !== action.payload.documentId ? a : { ...a, isFavorite: true })
                                })
                        }
                    }
                }
            }
        case GroupActionTypes.REMOVE_FAVORITE_DOCUMENT_COMPLETED:
            {
                return {
                    ...state,
                    meetings: {
                        ...state.meetings,
                        byId: {
                            ...state.meetings.byId,
                            [action.payload.groupId]: state.meetings.byId[action.payload.groupId]
                                .map(m => m.meetingId !== action.payload.meetingId ? m : {
                                    ...m, documents: m.documents.map(a => a.id !== action.payload.documentId ? a : { ...a, isFavorite: false })
                                })
                        }
                    }
                }
            }
        case GroupActionTypes.FETCH_GROUP_MEMBERS_STARTED: {
            if (state.members.allIds.indexOf(action.payload) > -1) {
                return {
                    ...state,
                    members: {
                        ...state.members,
                        byId: {
                            ...state.members.byId,
                            [action.payload]: {
                                ...state.members.byId[action.payload],
                                isLoading: true,
                            }
                        }
                    }
                }
            } else {
                return {
                    ...state,
                    members: {
                        allIds: [...state.members.allIds, action.payload.toString()],
                        byId: {
                            ...state.members.byId,
                            [action.payload]: {
                                isLoading: true,
                                members: {
                                    allIds: [],
                                    byId: {}
                                }
                            }
                        }
                    }
                }
            }
        }
        case GroupActionTypes.FETCH_GROUP_MEMBERS_COMPLETED: {
            return {
                ...state,
                members: {
                    allIds: state.members.allIds,
                    byId: {
                        ...state.members.byId,
                        [action.payload.id]: {
                            ...state.members.byId[action.payload.id],
                            isLoading: false,
                            members: {
                                allIds: action.payload.members.map((member) => (member.diNumber.toString())),
                                byId: action.payload.members.reduce((prev, member) => {
                                    prev[member.diNumber.toString()] = member;
                                    return prev;
                                }, {} as { [key: string]: IGroupMember })
                            }
                        }
                    }
                }
            }
        }
        case GroupActionTypes.FETCH_GROUP_MEMBERS_FAILED: {
            return {
                ...state,
                members: {
                    allIds: state.members.allIds,
                    byId: {
                        ...state.members.byId,
                        [action.payload as string]: {
                            ...state.members.byId[action.payload as string],
                            isLoading: false
                        }
                    }
                }
            }
        }
        case GroupActionTypes.FETCH_GROUP_MESSAGES_COMPLETED: {
            return {
                ...state,
                messages: {
                    ...state.messages,
                    allIds: addIfMissing(action.payload.id, state.meetings.allIds),
                    byId: {
                        ...state.messages.byId,
                        [action.payload.id]: action.payload.messages
                    }
                }
            }
        }
        case GroupActionTypes.FETCH_GROUPS_STARTED: {
            return {
                ...state,
                groups: {
                    ...state.groups,
                    isLoading: true
                }
            }
        }
        case GroupActionTypes.FETCH_GROUPS_COMPLETED: {
            return {
                ...state,
                groups: {
                    groups: {
                        allIds: action.payload.isDi ? [...state.groups.groups.allIds.concat(action.payload.groups.map(group => (group.id.toString()))).filter(unique)] : action.payload.groups.map(group => (group.id.toString())),
                        byId: action.payload.groups.reduce((prev, group) => {
                            prev[group.id.toString()] = group;
                            return prev;
                        }, { ...state.groups.groups.byId })
                    },
                    isLoading: false,
                    isLoaded: true
                }
            }
        }
        case GroupActionTypes.FETCH_GROUPS_FAILED: {
            return {
                ...state,
                groups: {
                    ...state.groups,
                    isLoading: false
                }
            }
        }
        case GroupActionTypes.GET_GROUP_COMPLETED: {
            return {
                ...state,
                currentGroup: action.payload.group.id,
                groups: {
                    ...state.groups,
                    groups: {
                        ...state.groups.groups,
                        allIds: addIfMissing(action.payload.group.id, state.groups.groups.allIds),
                        byId: {
                            ...state.groups.groups.byId,
                            [action.payload.group.id]: action.payload.group
                        }
                    }
                }
            }
        }
        case GroupActionTypes.SET_CURRENT_GROUP:
            {
                return {
                    ...state,
                    currentGroup: action.payload
                }
            }
        default: {
            return state;
        }
    }
}